import React, { useState, useMemo, useEffect } from 'react';
import './MileageEstimator.css';

export const MILEAGE_BAND_COLORS = {
    core: '#22c55e',     // green - Core / Range
    premium: '#a855f7',  // purple - Premium
    below: '#eab308'     // yellow - Below Average
};

const MileageEstimator = ({ filteredAuctions, onGraphToggleChange, onRegressionChange }) => {
    const [mileage, setMileage] = useState('');
    const [estimate, setEstimate] = useState(null);
    const [showOnGraph, setShowOnGraph] = useState(false);

    // Calculate linear regression coefficients
    const regressionData = useMemo(() => {
        if (!filteredAuctions || filteredAuctions.length < 5) {
            return null;
        }

        // Filter to only sold auctions with valid mileage and price
        const validAuctions = filteredAuctions.filter(a => 
            a.sold === true && 
            typeof a.miles === 'number' && 
            a.miles > 0 &&
            typeof a.price === 'number' && 
            a.price > 0
        );

        if (validAuctions.length < 5) {
            return null;
        }

        const n = validAuctions.length;
        
        // Calculate means
        const sumX = validAuctions.reduce((sum, a) => sum + a.miles, 0);
        const sumY = validAuctions.reduce((sum, a) => sum + a.price, 0);
        const meanX = sumX / n;
        const meanY = sumY / n;

        // Calculate slope (b) and intercept (a) for y = a + bx
        let numerator = 0;
        let denominator = 0;
        
        validAuctions.forEach(a => {
            numerator += (a.miles - meanX) * (a.price - meanY);
            denominator += (a.miles - meanX) ** 2;
        });

        // Avoid division by zero
        if (denominator === 0) {
            return null;
        }

        const slope = numerator / denominator;
        const intercept = meanY - slope * meanX;

        // Calculate R² (coefficient of determination)
        let ssRes = 0; // Sum of squares of residuals
        let ssTot = 0; // Total sum of squares
        
        validAuctions.forEach(a => {
            const predicted = intercept + slope * a.miles;
            ssRes += (a.price - predicted) ** 2;
            ssTot += (a.price - meanY) ** 2;
        });

        const rSquared = ssTot === 0 ? 0 : 1 - (ssRes / ssTot);

        // Calculate standard error of estimate
        const standardError = Math.sqrt(ssRes / (n - 2));

        // Get mileage range from data
        const mileages = validAuctions.map(a => a.miles);
        const minMileage = Math.min(...mileages);
        const maxMileage = Math.max(...mileages);

        // Price range of comps (used to clamp the estimate band)
        const prices = validAuctions.map(a => a.price);
        const minPrice = Math.min(...prices);
        const maxPrice = Math.max(...prices);

        return {
            slope,
            intercept,
            rSquared,
            standardError,
            sampleSize: n,
            minMileage,
            maxMileage,
            meanPrice: meanY,
            meanX,
            sxx: denominator,
            minPrice,
            maxPrice
        };
    }, [filteredAuctions]);

    // Determine confidence level based on sample size and R².
    // zMultiplier scales the prediction standard error into the +/- band.
    const getConfidence = (sampleSize, rSquared) => {
        if (sampleSize < 5) {
            return { level: 'none', label: 'Not enough data', color: '#999' };
        }
        if (sampleSize <= 5) {
            return { level: 'low', label: 'Low Confidence', color: '#f59e0b', zMultiplier: 1.15 };
        }
        if (sampleSize <= 15) {
            if (rSquared > 0.7) {
                return { level: 'medium', label: 'Medium Confidence', color: '#3b82f6', zMultiplier: 0.85 };
            }
            return { level: 'medium', label: 'Medium Confidence', color: '#3b82f6', zMultiplier: 1.0 };
        }
        // 16+ samples
        if (rSquared > 0.7) {
            return { level: 'high', label: 'High Confidence', color: '#22c55e', zMultiplier: 0.75 };
        }
        return { level: 'high', label: 'High Confidence', color: '#22c55e', zMultiplier: 0.85 };
    };

    const handleEstimate = () => {
        if (!regressionData || !mileage) {
            return;
        }

        const mileageNum = parseInt(mileage.replace(/,/g, ''), 10);
        if (isNaN(mileageNum) || mileageNum < 0) {
            return;
        }

        // Calculate predicted price
        let predictedPrice = regressionData.intercept + regressionData.slope * mileageNum;
        
        // Ensure price doesn't go negative
        predictedPrice = Math.max(predictedPrice, regressionData.meanPrice * 0.1);

        const confidence = getConfidence(regressionData.sampleSize, regressionData.rSquared);

        // Prediction standard error at the target mileage (textbook formula):
        // se_pred = s * sqrt(1 + 1/n + (x - meanX)^2 / Sxx)
        const { standardError, sampleSize, meanX, sxx, minPrice, maxPrice } = regressionData;
        const predictionSE = standardError * Math.sqrt(
            1 + (1 / sampleSize) + ((mileageNum - meanX) ** 2) / (sxx || 1)
        );

        const rangeAmount = confidence.zMultiplier * predictionSE;

        let lowEstimate = Math.max(0, predictedPrice - rangeAmount);
        let highEstimate = predictedPrice + rangeAmount;

        // Clamp the band to the actual comp price range so the estimate never
        // suggests prices well outside what the filtered market has produced.
        lowEstimate = Math.max(lowEstimate, minPrice);
        highEstimate = Math.min(highEstimate, maxPrice);

        // Guard against clamp inversion when predicted price is outside comp range
        if (lowEstimate > highEstimate) {
            lowEstimate = Math.min(minPrice, predictedPrice);
            highEstimate = Math.max(maxPrice, predictedPrice);
        }

        // Check if mileage is outside the data range (extrapolation warning)
        const isExtrapolating = mileageNum < regressionData.minMileage || mileageNum > regressionData.maxMileage;

        setEstimate({
            predicted: isExtrapolating ? '-' : predictedPrice,
            low: lowEstimate,
            high: highEstimate,
            confidence,
            sampleSize: regressionData.sampleSize,
            isExtrapolating,
            rSquared: regressionData.rSquared
        });
    };

    const formatPrice = (price) => {
        // Round to nearest hundred
        const rounded = Math.round(price / 100) * 100;
        return '$' + rounded.toLocaleString();
    };

    const handleMileageChange = (e) => {
        // Allow only numbers and commas
        const value = e.target.value.replace(/[^\d,]/g, '');
        setMileage(value);
        // Clear previous estimate when mileage changes
        setEstimate(null);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleEstimate();
        }
    };

    // Publish the regression summary (for chart overlay) and toggle state up to the parent
    useEffect(() => {
        if (!onGraphToggleChange) return;
        onGraphToggleChange(showOnGraph);
    }, [showOnGraph, onGraphToggleChange]);

    useEffect(() => {
        if (!onRegressionChange) return;
        if (!regressionData) {
            onRegressionChange(null);
            return;
        }
        const confidence = getConfidence(regressionData.sampleSize, regressionData.rSquared);
        onRegressionChange({
            slope: regressionData.slope,
            intercept: regressionData.intercept,
            standardError: regressionData.standardError,
            sampleSize: regressionData.sampleSize,
            meanX: regressionData.meanX,
            sxx: regressionData.sxx,
            zMultiplier: confidence.zMultiplier,
            minMileage: regressionData.minMileage,
            maxMileage: regressionData.maxMileage,
            minPrice: regressionData.minPrice,
            maxPrice: regressionData.maxPrice,
            colors: MILEAGE_BAND_COLORS
        });
    }, [regressionData, onRegressionChange]);

    // Show message if not enough data
    if (!regressionData) {
        return (
            <div className="mileage-estimator-tile">
                <h3 className="estimator-title">Price Guide</h3>
                <p className="estimator-no-data">
                    Not enough filtered results to estimate. 
                    Need at least 5 sold auctions.
                </p>
            </div>
        );
    }

    return (
        <div className="mileage-estimator-tile">
            <h3 className="estimator-title">Price Guide</h3>
            
            <div className="estimator-input-group">
                <label>Enter Mileage:</label>
                <input
                    type="text"
                    value={mileage}
                    onChange={handleMileageChange}
                    onKeyPress={handleKeyPress}
                    placeholder="e.g. 45,000"
                    className="mileage-input"
                />
            </div>

            <button 
                className="estimate-button"
                onClick={handleEstimate}
                disabled={!mileage}
            >
                Get Estimate
            </button>

            {estimate && (
                <div className="estimate-result">
                    <div className="estimate-price">
                        {estimate.predicted === '-' ? '-' : formatPrice(estimate.predicted)}
                    </div>
                    <div className="estimate-range">
                        Range: {formatPrice(estimate.low)} - {formatPrice(estimate.high)}
                    </div>
                    <div 
                        className="estimate-confidence"
                        style={{ color: estimate.confidence.color }}
                    >
                        {estimate.confidence.level === 'high' && '✓✓ '}
                        {estimate.confidence.level === 'medium' && '✓ '}
                        {estimate.confidence.level === 'low' && '⚠️ '}
                        {estimate.confidence.label}
                    </div>
                    <div className="estimate-sample-size">
                        Based on {estimate.sampleSize} result{estimate.sampleSize !== 1 ? 's' : ''}
                    </div>
                    {estimate.isExtrapolating && (
                        <div className="estimate-warning">
                            ⚠️ Mileage outside data range
                        </div>
                    )}
                </div>
            )}

            <div className="condition-graph-toggle-row">
                <span className="condition-graph-toggle-label">Show on Graph</span>
                <label className="switch">
                    <input
                        type="checkbox"
                        checked={showOnGraph}
                        onChange={(e) => setShowOnGraph(e.target.checked)}
                    />
                    <span className="slider round"></span>
                </label>
            </div>
        </div>
    );
};

export default MileageEstimator;
